import { Trans } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import {
  BaseActivity,
  BaseActivityArticleName,
  BaseActivityDetailedContent,
  BaseActivityText,
} from '@/components/activities/BaseActivity/BaseActivity';
import InlineText from '@/design_system/Typography/InlineText';
import IconInfo from '@/icons/Info.svg';

export const ArticleCreationAcceptedActivity = ({
  activity,
  displayArticleName,
}: {
  activity: ActivityOfType<'article_creation_accepted'>;
  displayArticleName: boolean;
}) => {
  const creatorName = activity.creator.name;

  return (
    <BaseActivity
      key={activity.id}
      createdAt={activity.createdAt}
      icon={<IconInfo />}
      message={
        <BaseActivityText>
          {activity.data.acceptedAsException ? (
            <AcceptedTheItemAsException
              activity={activity}
              displayArticleName={displayArticleName}
              creatorName={creatorName}
            />
          ) : (
            <AddedItem
              activity={activity}
              displayArticleName={displayArticleName}
              creatorName={creatorName}
            />
          )}{' '}
          <PurchasedWithinNetwork activity={activity} /> <Brand activity={activity} />
          {activity.data.warranty !== undefined && (
            <>
              {' '}
              <Warranty activity={activity} />
            </>
          )}
        </BaseActivityText>
      }
    >
      {!!activity.data.exceptionReason && (
        <BaseActivityDetailedContent>{activity.data.exceptionReason}</BaseActivityDetailedContent>
      )}
    </BaseActivity>
  );
};

const AcceptedTheItemAsException = ({
  activity,
  displayArticleName,
  creatorName,
}: {
  activity: ActivityOfType<'article_creation_accepted'>;
  displayArticleName: boolean;
  creatorName: string;
}) => {
  const articleName = displayArticleName ? (
    <BaseActivityArticleName activity={activity} displayArticleName={displayArticleName} />
  ) : null;

  return (
    <Trans id="activities.article-creation-accepted.accepted-as-exception.message">
      <InlineText weight="medium">{creatorName}</InlineText> accepted the item {articleName}as an
      exception to service eligibility rules.
    </Trans>
  );
};

const AddedItem = ({
  activity,
  displayArticleName,
  creatorName,
}: {
  activity: ActivityOfType<'article_creation_accepted'>;
  displayArticleName: boolean;
  creatorName: string;
}) => {
  const articleNameWithSpace = displayArticleName ? (
    <>
      {' '}
      <BaseActivityArticleName activity={activity} displayArticleName={displayArticleName} />
    </>
  ) : null;

  return (
    <Trans id="activities.article-creation-accepted.added-item.message">
      <InlineText weight="medium">{creatorName}</InlineText> added item{articleNameWithSpace}.
    </Trans>
  );
};

const PurchasedWithinNetwork = ({
  activity,
}: {
  activity: ActivityOfType<'article_creation_accepted'>;
}) => {
  return (
    <>
      <InlineText weight="medium">
        <Trans id="activities.article-creation-accepted.purchased-within-network.message">
          Purchased within network:
        </Trans>
      </InlineText>{' '}
      {activity.data.soldThroughNetwork ? (
        <Trans id="activities.article-creation-accepted.purchased-within-network.yes">Yes.</Trans>
      ) : (
        <Trans id="activities.article-creation-accepted.purchased-within-network.no">No.</Trans>
      )}
    </>
  );
};

const Brand = ({ activity }: { activity: ActivityOfType<'article_creation_accepted'> }) => {
  return (
    <>
      <InlineText weight="medium">
        <Trans id="activities.article-creation-accepted.brand.message">Brand:</Trans>
      </InlineText>{' '}
      {activity.data.brand.name}.
    </>
  );
};

const Warranty = ({ activity }: { activity: ActivityOfType<'article_creation_accepted'> }) => {
  return (
    <>
      {' '}
      <InlineText weight="medium">
        <Trans id="activities.article-creation-accepted.warranty.message">Under warranty:</Trans>
      </InlineText>{' '}
      {activity.data.warranty ? (
        <Trans id="activities.article-creation-accepted.warranty.yes">Yes.</Trans>
      ) : (
        <Trans id="activities.article-creation-accepted.warranty.no">No.</Trans>
      )}
    </>
  );
};
